import React, { useState, useEffect } from 'react';
import api from '../config/api';
import PageNavbar from './PageNavbar';
import './GrazingAdmin.css';

const EMPTY_CATEGORY = { name: '', sort_order: '0' };
const EMPTY_ITEM = { name: '', default_price: '', unit: 'portion', sort_order: '0' };

const GrazingAdmin = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  const [showCatForm, setShowCatForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [catForm, setCatForm] = useState(EMPTY_CATEGORY);

  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState(EMPTY_ITEM);

  const fetchCatalog = async () => {
    try {
      const response = await api.get('/grazing/catalog?all=1');
      const cats = response.data.categories || [];
      setCategories(cats);
      setSelectedCategoryId((prev) => {
        if (prev && cats.some((c) => String(c.id) === String(prev))) return prev;
        return cats[0]?.id ?? null;
      });
    } catch (error) {
      console.error('Error fetching grazing catalog:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCatalog();
  }, []);

  const selectedCategory = categories.find((c) => String(c.id) === String(selectedCategoryId));

  const resetCatForm = () => {
    setCatForm(EMPTY_CATEGORY);
    setEditingCategory(null);
    setShowCatForm(false);
  };

  const resetItemForm = () => {
    setItemForm(EMPTY_ITEM);
    setEditingItem(null);
    setShowItemForm(false);
  };

  const handleEditCategory = (cat) => {
    setEditingCategory(cat);
    setCatForm({
      name: cat.name || '',
      sort_order: cat.sort_order != null ? String(cat.sort_order) : '0'
    });
    setShowCatForm(true);
  };

  const handleSubmitCategory = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: catForm.name.trim(),
        sort_order: parseInt(catForm.sort_order, 10) || 0
      };
      if (editingCategory) {
        await api.put(`/grazing/categories/${editingCategory.id}`, payload);
        alert('Category updated');
      } else {
        await api.post('/grazing/categories', payload);
        alert('Category created');
      }
      resetCatForm();
      fetchCatalog();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to save category');
    }
  };

  const handleToggleCategory = async (cat) => {
    if (!window.confirm(`${cat.active ? 'Deactivate' : 'Activate'} category "${cat.name}"?`)) return;
    try {
      await api.put(`/grazing/categories/${cat.id}`, { active: !cat.active });
      fetchCatalog();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update category');
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      name: item.name || '',
      default_price: item.default_price != null ? String(item.default_price) : '',
      unit: item.unit || 'portion',
      sort_order: item.sort_order != null ? String(item.sort_order) : '0'
    });
    setShowItemForm(true);
  };

  const handleSubmitItem = async (e) => {
    e.preventDefault();
    if (!selectedCategoryId) {
      alert('Select a category first');
      return;
    }
    try {
      const payload = {
        category_id: selectedCategoryId,
        name: itemForm.name.trim(),
        default_price: itemForm.default_price.trim() !== '' ? parseFloat(itemForm.default_price) : 0,
        unit: itemForm.unit.trim() || 'portion',
        sort_order: parseInt(itemForm.sort_order, 10) || 0
      };
      if (editingItem) {
        await api.put(`/grazing/items/${editingItem.id}`, payload);
        alert('Item updated');
      } else {
        await api.post('/grazing/items', payload);
        alert('Item created');
      }
      resetItemForm();
      fetchCatalog();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to save item');
    }
  };

  const handleToggleItem = async (item) => {
    if (!window.confirm(`${item.active ? 'Deactivate' : 'Activate'} item "${item.name}"?`)) return;
    try {
      await api.put(`/grazing/items/${item.id}`, { active: !item.active });
      fetchCatalog();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update item');
    }
  };

  return (
    <div className="grazing-admin-page">
      <PageNavbar />
      <div className="container">
        <div className="grazing-admin-header">
          <h1>Grazing Tables</h1>
        </div>
        <p className="grazing-admin-hint">
          Manage grazing categories and items used on Events / N Caterers orders (alongside desserts).
        </p>

        {loading ? (
          <div className="loading">Loading catalog...</div>
        ) : (
          <div className="grazing-admin-layout">
            <div className="card grazing-admin-cats">
              <div className="grazing-admin-subheader">
                <h2>Categories</h2>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    if (showCatForm) resetCatForm();
                    else {
                      resetCatForm();
                      setShowCatForm(true);
                    }
                  }}
                >
                  {showCatForm && !editingCategory ? 'Cancel' : 'Add Category'}
                </button>
              </div>

              {showCatForm && (
                <form onSubmit={handleSubmitCategory} className="grazing-inline-form">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={catForm.name}
                      onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
                      required
                      placeholder="e.g. Cheese Board"
                    />
                  </div>
                  <div className="form-group">
                    <label>Sort order</label>
                    <input
                      type="number"
                      className="form-control"
                      value={catForm.sort_order}
                      onChange={(e) => setCatForm({ ...catForm, sort_order: e.target.value })}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="submit" className="btn btn-primary btn-sm">
                      {editingCategory ? 'Update' : 'Create'}
                    </button>
                    {editingCategory && (
                      <button type="button" className="btn btn-secondary btn-sm" onClick={resetCatForm}>
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              )}

              {categories.length === 0 ? (
                <p className="no-data">No categories yet.</p>
              ) : (
                <ul className="grazing-cat-list">
                  {categories.map((cat) => (
                    <li
                      key={cat.id}
                      className={`grazing-cat-item${String(cat.id) === String(selectedCategoryId) ? ' is-selected' : ''}${!cat.active ? ' is-inactive' : ''}`}
                    >
                      <button
                        type="button"
                        className="grazing-cat-select"
                        onClick={() => {
                          setSelectedCategoryId(cat.id);
                          resetItemForm();
                        }}
                      >
                        <strong>{cat.name}</strong>
                        <span>{(cat.items || []).length} items</span>
                      </button>
                      <div className="grazing-cat-actions">
                        <button type="button" className="btn btn-sm btn-primary" onClick={() => handleEditCategory(cat)}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${cat.active ? 'btn-secondary' : 'btn-success'}`}
                          onClick={() => handleToggleCategory(cat)}
                        >
                          {cat.active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="card grazing-admin-items">
              <div className="grazing-admin-subheader">
                <h2>
                  Items{selectedCategory ? ` — ${selectedCategory.name}` : ''}
                </h2>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={!selectedCategoryId}
                  onClick={() => {
                    if (showItemForm) resetItemForm();
                    else {
                      resetItemForm();
                      setShowItemForm(true);
                    }
                  }}
                >
                  {showItemForm && !editingItem ? 'Cancel' : 'Add Item'}
                </button>
              </div>

              {showItemForm && (
                <form onSubmit={handleSubmitItem} className="grazing-inline-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={itemForm.name}
                        onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                        required
                        placeholder="Item name"
                      />
                    </div>
                    <div className="form-group">
                      <label>Default price (₹)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-control"
                        value={itemForm.default_price}
                        onChange={(e) => setItemForm({ ...itemForm, default_price: e.target.value })}
                        placeholder="0"
                      />
                    </div>
                    <div className="form-group">
                      <label>Unit</label>
                      <input
                        type="text"
                        className="form-control"
                        value={itemForm.unit}
                        onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })}
                        placeholder="portion / kg / tray"
                      />
                    </div>
                    <div className="form-group">
                      <label>Sort order</label>
                      <input
                        type="number"
                        className="form-control"
                        value={itemForm.sort_order}
                        onChange={(e) => setItemForm({ ...itemForm, sort_order: e.target.value })}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="submit" className="btn btn-primary btn-sm">
                      {editingItem ? 'Update Item' : 'Add Item'}
                    </button>
                    {editingItem && (
                      <button type="button" className="btn btn-secondary btn-sm" onClick={resetItemForm}>
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              )}

              {!selectedCategory ? (
                <p className="no-data">Select a category to manage items.</p>
              ) : !(selectedCategory.items || []).length ? (
                <p className="no-data">No items in this category yet.</p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Price</th>
                      <th>Unit</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedCategory.items || []).map((item) => (
                      <tr key={item.id} className={item.active ? '' : 'grazing-item-inactive'}>
                        <td><strong>{item.name}</strong></td>
                        <td>₹{item.default_price != null ? Number(item.default_price).toFixed(2) : '0.00'}</td>
                        <td>{item.unit || '-'}</td>
                        <td>
                          <span className={`status-badge ${item.active ? 'status-active' : 'status-inactive'}`}>
                            {item.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            style={{ marginRight: 8 }}
                            onClick={() => handleEditItem(item)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className={`btn btn-sm ${item.active ? 'btn-secondary' : 'btn-success'}`}
                            onClick={() => handleToggleItem(item)}
                          >
                            {item.active ? 'Deactivate' : 'Activate'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GrazingAdmin;
